import { MenuItem, Stack, TextField, Typography } from '@mui/material';
import {
  FileUpload,
  FormSection,
  ControllerDatePicker,
  ControllerSelect,
  ControllerAutoComplete,
  RadioButtons,
  InfoText,
} from 'Components';
import { DICTIONARY, FileCategories } from 'Enum';
import { Dictionary } from 'Dictionary';
import { useFormContext, useWatch } from 'react-hook-form';
import { useAppSelector, useDictionary, useIsDesktop } from 'Hooks';
import { generateError } from 'Regex';
import { textFieldShrink } from 'Utils';
import { IPersonalDetails } from 'API';
import {
  validateEnglishLettersOnly,
  validateHebrewLettersOnly,
} from 'Utils/validateLettersAndNumbersOnly';
import { validateNumbersOnly } from 'Utils/validateNumbersOnly';
import { store } from 'Store';
import { FormSectionWrapper } from 'Components/FormSectionWrapper';
import { styleTextField } from 'Theme';

export const ISRAEL_ID = 124;

export const FirstSection = () => {
  const {
    register,
    getValues,
    formState: { errors },
  } = useFormContext<IPersonalDetails>();
  const [maritalStatus, countries, genders] = useAppSelector(
    ({
      generalContent: {
        generalContent: { maritalStatuses, genders },
        countries,
      },
    }) => [maritalStatuses, countries, genders]
  );

  const formInfo = store.getState().generalContent.formInfo;
  const firstNameFormName = 'personalInfoSection.firstName';
  const lastNameFormName = 'personalInfoSection.lastName';
  const firstNameEnglishForm = 'personalInfoSection.firstNameEnglish';
  const lastNameEnglishForm = 'personalInfoSection.lastNameEnglish';
  const [firstName, lastName, firstNameEnglish, lastNameEnglish] = getValues([
    firstNameFormName,
    lastNameFormName,
    firstNameEnglishForm,
    lastNameEnglishForm,
  ]);

  const isDesktop = useIsDesktop();
  const ADULT_AT_LEAST_18_YEARS_AGO = new Date(
    new Date().setFullYear(new Date().getFullYear() - 18)
  );

  const personalInfoSectionErrors = errors.personalInfoSection;
  const dictionary = useDictionary(Dictionary, DICTIONARY.PERSONAL_DETAILS);

  const birthCountryId = useWatch({
    name: 'personalInfoSection.birthCountryId',
  });

  return (
    <FormSectionWrapper>
      <FormSection>
        <Stack gap='20px'>
          <TextField
            id={dictionary.firstSectionFirstInput}
            inputProps={{
              maxLength: '20',
            }}
            {...register(firstNameFormName)}
            error={Boolean(personalInfoSectionErrors?.firstName)}
            label={dictionary.firstSectionFirstInput}
            {...textFieldShrink(firstName, true)}
            helperText={
              Boolean(personalInfoSectionErrors?.firstName) &&
              generateError(
                dictionary.firstSectionFirstInput,
                personalInfoSectionErrors?.firstName?.message!
              )
            }
            role={
              Boolean(personalInfoSectionErrors?.firstName)
                ? 'alert'
                : undefined
            }
            onChange={(event: any) => validateHebrewLettersOnly(event)}
            sx={styleTextField}
          />
          <TextField
            role={
              Boolean(personalInfoSectionErrors?.lastName) ? 'alert' : undefined
            }
            id={dictionary.firstSectionSecondInput}
            inputProps={{
              maxLength: '40',
            }}
            {...register(lastNameFormName)}
            label={dictionary.firstSectionSecondInput}
            {...textFieldShrink(lastName, true)}
            error={Boolean(personalInfoSectionErrors?.lastName)}
            helperText={
              Boolean(personalInfoSectionErrors?.lastName) &&
              generateError(
                dictionary.firstSectionSecondInput,
                personalInfoSectionErrors?.lastName?.message!
              )
            }
            onChange={(event: any) => validateHebrewLettersOnly(event)}
            sx={styleTextField}
          />
          <TextField
            id={dictionary.firstSectionEnglishFirstName}
            inputProps={{
              maxLength: '30',
            }}
            {...register(firstNameEnglishForm)}
            error={Boolean(personalInfoSectionErrors?.firstNameEnglish)}
            label={dictionary.firstSectionEnglishFirstName}
            {...textFieldShrink(firstNameEnglish, true)}
            helperText={
              Boolean(personalInfoSectionErrors?.firstNameEnglish) &&
              generateError(
                dictionary.firstSectionEnglishFirstName,
                personalInfoSectionErrors?.firstNameEnglish?.message!
              )
            }
            role={
              Boolean(personalInfoSectionErrors?.firstNameEnglish)
                ? 'alert'
                : undefined
            }
            onChange={(event: any) => validateEnglishLettersOnly(event)}
            sx={styleTextField}
          />
          <TextField
            id={dictionary.firstSectionEnglishLastName}
            inputProps={{
              maxLength: '30',
            }}
            {...register(lastNameEnglishForm)}
            error={Boolean(personalInfoSectionErrors?.lastNameEnglish)}
            label={dictionary.firstSectionEnglishLastName}
            {...textFieldShrink(lastNameEnglish, true)}
            helperText={
              Boolean(personalInfoSectionErrors?.lastNameEnglish) &&
              generateError(
                dictionary.firstSectionEnglishLastName,
                personalInfoSectionErrors?.lastNameEnglish?.message!
              )
            }
            role={
              Boolean(personalInfoSectionErrors?.lastNameEnglish)
                ? 'alert'
                : undefined
            }
            onChange={(event: any) => validateEnglishLettersOnly(event)}
            sx={styleTextField}
          />
          <TextField
            id={dictionary.firstSectionThirdInput}
            {...register('personalInfoSection.israeliId')}
            InputLabelProps={{ shrink: true }}
            disabled
            label={dictionary.firstSectionThirdInput}
            onKeyDown={(event) => validateNumbersOnly(event)}
            sx={styleTextField}
          />
        </Stack>

        {!formInfo.isRecognized && (
          <>
            <Typography>{dictionary.firstSectionFileUploadLabel}</Typography>
            <InfoText text={dictionary.firstSectionFileUploadLabelNote} />
            <FileUpload
              fileLabel={dictionary.firstSectionFileUploadText}
              fileCategoryId={FileCategories.IdNumberScan}
              ariaLabel={dictionary.firstSectionFileUploadLabel}
              dataRequired
            />
          </>
        )}

        <Stack gap={isDesktop ? '56px' : '40px'} pb='32px'>
          <RadioButtons
            id={dictionary.firstSectionToggleButtonLabel}
            name='personalInfoSection.genderId'
            options={genders.map(({ value, id }) => ({
              value: id.toString(),
              label: value,
            }))}
          />
          <Stack gap={isDesktop ? '40px' : '26px'}>
            <ControllerSelect
              name='personalInfoSection.maritalStatusId'
              label={dictionary.firstSectionFourthInput}
            >
              {maritalStatus.map(({ id, value }) => (
                <MenuItem key={id} value={id}>
                  {value}
                </MenuItem>
              ))}
            </ControllerSelect>
            <ControllerDatePicker
              label={dictionary.firstSectionFifthInput}
              name='personalInfoSection.birthDate'
              required
              maxDate={ADULT_AT_LEAST_18_YEARS_AGO}
            />
            <ControllerAutoComplete
              options={countries}
              name='personalInfoSection.birthCountryId'
              label={dictionary.firstSectionSixthInput}
            />
            {birthCountryId && birthCountryId !== ISRAEL_ID && (
              <ControllerDatePicker
                viewYearOnly
                label={dictionary.firstSectionSeventhInput}
                name='personalInfoSection.immigrationYear'
              />
            )}
          </Stack>
        </Stack>
      </FormSection>
    </FormSectionWrapper>
  );
};
